w<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <b-form>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">{{ $t("dmp.holidayDates.title") }}</h5>
              <div class="d-flex gap-1">
                <b-button variant="primary" size="sm" @click="addNewHoliday" :disabled="isLoadingHolidays"
                  class="text-capitalize">
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  {{ $t("dmp.holidayDates.addHoliday") }}
                </b-button>
              </div>
            </div>

            <div v-if="isLoadingHolidays" class="text-center py-4">
              <b-spinner variant="primary" :label="$t('dmp.holidayDates.loadingHolidays')"></b-spinner>
              <p class="mt-2 mb-0">{{ $t("dmp.holidayDates.loadingHolidays") }}</p>
            </div>

            <b-row v-if="!isLoadingHolidays">
              <b-col cols="7" class="table-column">
                <!-- Holidays Table -->
                <b-table :items="displayHolidays" :fields="holidayTableFields" responsive striped hover show-empty
                  :empty-text="$t('dmp.holidayDates.noHolidaysConfigured')" selectable select-mode="single"
                  @row-selected="onRowSelected" sort-by="number" :sort-desc="false" class="mb-0">
                  <template #cell(number)="data">
                    <span>{{ data.value }}</span>
                  </template>

                  <template #cell(class)="data">
                    <span>{{ data.value }}</span>
                  </template>

                  <template #cell(date)="data">
                    <span>{{ formatDateForDisplay(data.value) }}</span>
                  </template>

                  <template #cell(name)="data">
                    <span>{{ data.value || "-" }}</span>
                  </template>
                </b-table>
              </b-col>

              <b-col cols="5" class="details-column">
                <!-- Selected Holiday Details Card -->
                <b-card v-if="selectedHoliday" no-body>
                  <b-card-header class="mb-1 card-header-custom pt-0">
                    <h6 class="mb-0">{{ $t("dmp.holidayDates.holidayDetails") }}</h6>
                  </b-card-header>
                  <b-card-body>
                    <validation-observer ref="holidayFormObserver">
                      <b-form @submit.prevent="saveHoliday">
                        <b-row>
                          <b-col cols="12">
                            <validation-provider #default="{ errors }" :name="$t('dmp.holidayDates.number')"
                              rules="required|integer|min_value:1|max_value:20">
                              <b-form-group :label="$t('dmp.holidayDates.number')" label-for="holiday-number">
                                <b-form-input id="holiday-number" v-model.number="selectedHoliday.dateNumber"
                                  type="number" :state="getValidationState(errors)" min="1" max="20"
                                  :disabled="isEditingHoliday" />
                                <b-form-text class="text-muted">
                                  <small>Range: 1-20</small>
                                </b-form-text>
                                <b-form-invalid-feedback>
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12">
                            <validation-provider #default="{ errors }" :name="$t('dmp.holidayDates.name')"
                              rules="max:50">
                              <b-form-group :label="$t('dmp.holidayDates.name')" label-for="holiday-name">
                                <b-form-input id="holiday-name" v-model="selectedHoliday.name"
                                  :state="getValidationState(errors)" maxlength="50" />
                                <b-form-invalid-feedback>
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12">
                            <validation-provider #default="{ errors }" :name="$t('dmp.holidayDates.class')"
                              rules="required">
                              <b-form-group :label="$t('dmp.holidayDates.class')" label-for="holiday-class">
                                <b-form-select id="holiday-class" v-model="selectedHoliday.class"
                                  :options="classOptions" :state="getValidationState(errors)" />
                                <b-form-invalid-feedback>
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12">
                            <validation-provider #default="{ errors }" name="Holiday" rules="required">
                              <b-form-group label="Holiday" label-for="holiday-date">
                                <flat-pickr id="holiday-date" v-model="selectedHoliday.dateDisplay"
                                  :config="datePickerConfig" class="form-control" placeholder="Select date"
                                  @on-change="handleDateChange" />
                                <b-form-text class="text-muted">
                                  <small>Format: DD/MM</small>
                                </b-form-text>
                                <b-form-invalid-feedback :state="getValidationState(errors)">
                                  {{ errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>



                          <!-- Action Buttons -->
                          <b-col cols="12" class="d-flex justify-content-end mt-2">
                            <b-button v-if="isEditingHoliday" variant="outline-danger" size="sm" class="mr-1"
                              @click="deleteHoliday" :disabled="isSavingHoliday || isDeletingHoliday">
                              <b-spinner v-if="isDeletingHoliday" small class="mr-25" />
                              <feather-icon v-else icon="TrashIcon" size="14" class="mr-25" />
                              <span>{{ $t("actions.delete") }}</span>
                            </b-button>
                            <b-button variant="primary" size="sm" type="submit"
                              :disabled="isSavingHoliday || isDeletingHoliday">
                              <b-spinner v-if="isSavingHoliday" small class="mr-25" />
                              <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                              <span>{{
                                isSavingHoliday
                                  ? $t("actions.saving")
                                  : $t("actions.save")
                              }}</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-form>
                    </validation-observer>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import flatPickr from "vue-flatpickr-component";
import store from "@/store";
import { useTemplateMode } from "./useTemplateMode";
import { useToast } from "vue-toastification/composition";
import Swal from "sweetalert2";

export default {
  name: "ConfigHolidayDates",
  components: {
    ValidationObserver,
    ValidationProvider,
    flatPickr,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    holidayDatesData: {
      type: Object,
      default: () => ({}),
    },
    holidayDatesConfig: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;

    const { t } = useI18nUtils();
    const toast = useToast();
    const { refFormObserver, getValidationState } = formValidation();
    const { isTemplateMode, emitTemplateUpdate } = useTemplateMode(props, emit);

    // Constants
    const EMPTY_DATE = "0000";
    const DEFAULT_CLASS = "A";
    const MAX_HOLIDAYS = 20;

    // Reactive data
    const holidays = ref([]);
    const isLoadingHolidays = computed(() => props.loading);
    const isSavingHoliday = ref(false);
    const isDeletingHoliday = ref(false);
    const isEditingHoliday = ref(false);
    const holidayFormObserver = ref(null);
    const selectedHoliday = ref(null);

    const datePickerConfig = {
      dateFormat: "d/m",
      allowInput: true,
      locale: {
        firstDayOfWeek: 1,
      },
    };

    const holidayTableFields = computed(() => [
      { key: "number", label: proxy.$t("dmp.holidayDates.number"), sortable: true },
      { key: "class", label: proxy.$t("dmp.holidayDates.class"), sortable: true },
      { key: "date", label: proxy.$t("dmp.holidayDates.date"), sortable: true },
      { key: "name", label: proxy.$t("dmp.holidayDates.name"), sortable: true },
    ]);

    const classOptions = computed(() => [
      { value: "", text: proxy.$t("dmp.holidayDates.selectClass"), disabled: true },
      { value: "A", text: "Class A" },
      { value: "B", text: "Class B" },
      { value: "C", text: "Class C" },
    ]);

    const displayHolidays = computed(() => {
      return holidays.value
        .filter(
          (holiday) => holiday.date !== EMPTY_DATE || holiday.class !== DEFAULT_CLASS
        )
        .map((holiday) => {
          let name = holiday.name || "";

          if (isTemplateMode.value && props.holidayDatesConfig.holiday_dates?.length) {
            const configItem = props.holidayDatesConfig.holiday_dates.find(
              (c) => parseInt(c.id) === parseInt(holiday.dateNumber)
            );
            name = configItem ? (configItem.description || "") : "";
          }

          return {
            dateNumber: holiday.dateNumber,
            number: holiday.dateNumber,
            class: holiday.class,
            date: holiday.date,
            name,
          };
        });
    });

    const formatDateForDisplay = (date) => {
      if (!date || date.length !== 4 || date === EMPTY_DATE) {
        return "";
      }
      const day = date.substring(0, 2);
      const month = date.substring(2, 4);
      return `${day}/${month}`;
    };

    const formatDateForStorage = (dateString) => {
      // dateString comes as "DD/MM" from flatpickr
      if (!dateString) return "";
      const parts = dateString.split("/");
      if (parts.length !== 2) return "";
      const day = parts[0].padStart(2, "0");
      const month = parts[1].padStart(2, "0");
      return `${day}${month}`; // DDMM format
    };

    const handleDateChange = (selectedDates, dateStr) => {
      if (selectedHoliday.value) {
        selectedHoliday.value.date = formatDateForStorage(dateStr);
      }
    };

    const showToast = (variant, title, text, icon) => {
      try {
        toast({
          component: ToastificationContent,
          props: { title, text, variant, icon },
        });
      } catch (error) {
        console.error("Error showing toast:", error);
      }
    };

    const showSuccess = (text) => showToast("success", "Success", text, "CheckIcon");
    const showError = (text) => showToast("danger", "Error", text, "AlertCircleIcon");
    const showWarning = (text, title = "Warning") =>
      showToast("warning", title, text, "AlertTriangleIcon");

    const loadHolidays = () => {
      if (!props.holidayDatesData || !props.holidayDatesData.dates) {
        holidays.value = [];
        return;
      }

      try {
        holidays.value = props.holidayDatesData.dates.map((date, index) => ({
          ...date,
          dateNumber: isTemplateMode.value ? index + 1 : date.dateNumber,
        }));
      } catch (error) {
        console.error("Error loading holidays:", error);
        holidays.value = [];
      }
    };

    const addNewHoliday = () => {
      isEditingHoliday.value = false;
      selectedHoliday.value = {
        dateNumber: null,
        class: DEFAULT_CLASS,
        date: "",
        dateDisplay: "",
        name: "",
      };
    };

    const onRowSelected = (items) => {
      if (items.length > 0) {
        const selected = items[0];

        let name = selected.name || "";
        if (isTemplateMode.value) {
          const configItem = props.holidayDatesConfig.holiday_dates.find(
            (c) => parseInt(c.id) === parseInt(selected.dateNumber)
          );
          name = configItem ? (configItem.description || "") : "";
        }

        isEditingHoliday.value = true;
        selectedHoliday.value = {
          dateNumber: selected.dateNumber,
          class: selected.class,
          date: selected.date,
          dateDisplay: formatDateForDisplay(selected.date),
          name,
        };
      } else {
        selectedHoliday.value = null;
        isEditingHoliday.value = false;
      }
    };

    const deleteHoliday = async () => {
      if (!isEditingHoliday.value) {
        return;
      }

      const result = await Swal.fire({
        title: proxy.$t("dmp.holidayDates.messages.deleteConfirm"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("actions.delete"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) {
        return;
      }

      if (isTemplateMode.value) {
        const dateNumber = selectedHoliday.value.dateNumber;
        const dates = Array.from({ length: MAX_HOLIDAYS }, (_, i) => {
          const num = i + 1;
          const ex = holidays.value.find((h) => h.dateNumber === num);
          if (ex && num !== dateNumber) {
            return { date: ex.date, class: ex.class };
          }
          return { date: EMPTY_DATE, class: DEFAULT_CLASS };
        });
        const item = {
          ...(props.holidayDatesData || {}),
          dates,
        };

        const configList = (props.holidayDatesConfig.holiday_dates || []).filter(
          (c) => parseInt(c.id) !== parseInt(dateNumber)
        );

        emit("template-update", {
          holiday_dates: item,
          config: { ...props.holidayDatesConfig, holiday_dates: configList },
        });

        showSuccess(proxy.$t("dmp.holidayDates.messages.deleteSuccess"));
        selectedHoliday.value = null;
        isEditingHoliday.value = false;
        return;
      }

      isDeletingHoliday.value = true;
      store.commit("dmp_panels/setOperationActive", true);

      try {
        const dateNumber = selectedHoliday.value.dateNumber;

        // Create array of 20 dates with all as empty except the ones we want to keep
        const dates = Array.from({ length: MAX_HOLIDAYS }, (_, i) => {
          const num = i + 1;
          const existingHoliday = holidays.value.find((h) => h.dateNumber === num);

          if (existingHoliday && num !== dateNumber) {
            return {
              class: existingHoliday.class,
              date: existingHoliday.date,
            };
          }

          return {
            class: DEFAULT_CLASS,
            date: EMPTY_DATE,
          };
        });

        const payload = {
          dateNumber: String(dateNumber),
          name: "",
          class: DEFAULT_CLASS,
          date: EMPTY_DATE,
          dates: dates,
        };

        const response = await store.dispatch("dmp_panels/deleteHolidayDate", {
          uuid: props.uuid,
          ...payload,
        });

        if (response?.success) {
          emit("operation-completed");

          showSuccess(proxy.$t("dmp.holidayDates.messages.deleteSuccess"));
          selectedHoliday.value = null;
          isEditingHoliday.value = false;
        } else {
          showError(
            response?.message || proxy.$t("dmp.holidayDates.messages.deleteError")
          );
        }
      } catch (error) {
        console.error("Error deleting holiday:", error);
        emit("operation-error", error);
        showError(proxy.$t("dmp.holidayDates.messages.deleteError"));
      } finally {
        isDeletingHoliday.value = false;
        store.commit("dmp_panels/setOperationActive", false);
      }
    };

    const saveHoliday = async () => {
      if (!holidayFormObserver.value) {
        console.error("holidayFormObserver is null");
        return;
      }

      const isValid = await holidayFormObserver.value.validate();
      if (!isValid) {
        return;
      }

      if (isTemplateMode.value) {
        const dates = Array.from({ length: MAX_HOLIDAYS }, (_, i) => {
          const num = i + 1;
          if (num === selectedHoliday.value.dateNumber) {
            return { date: selectedHoliday.value.date, class: selectedHoliday.value.class };
          }
          const ex = holidays.value.find((h) => h.dateNumber === num);
          return ex
            ? { date: ex.date, class: ex.class }
            : { date: EMPTY_DATE, class: DEFAULT_CLASS };
        });

        const item = {
          ...(props.holidayDatesData || {}),
          dates,
          subCommand: (props.holidayDatesData && props.holidayDatesData.subCommand) || "1",
        };

        const configList = [...(props.holidayDatesConfig.holiday_dates || [])];
        const configIdx = configList.findIndex(
          (c) => parseInt(c.id) === parseInt(selectedHoliday.value.dateNumber)
        );
        const configItem = {
          id: selectedHoliday.value.dateNumber,
          description: selectedHoliday.value.name || "",
        };
        if (configIdx !== -1) {
          configList.splice(configIdx, 1, configItem);
        } else {
          configList.push(configItem);
        }

        emit("template-update", {
          holiday_dates: item,
          config: { ...props.holidayDatesConfig, holiday_dates: configList },
        });

        showSuccess(
          isEditingHoliday.value
            ? proxy.$t("dmp.holidayDates.messages.updateSuccess")
            : proxy.$t("dmp.holidayDates.messages.saveSuccess")
        );
        selectedHoliday.value = null;
        isEditingHoliday.value = false;
        return;
      }

      isSavingHoliday.value = true;
      store.commit("dmp_panels/setOperationActive", true);

      try {
        // Create array of 20 dates with default values
        const dates = Array.from({ length: MAX_HOLIDAYS }, (_, i) => {
          const num = i + 1;
          const existingHoliday = holidays.value.find((h) => h.dateNumber === num);

          // If we're editing/creating this number, skip it (it will be in the top level)
          if (num === selectedHoliday.value.dateNumber) {
            return {
              class: DEFAULT_CLASS,
              date: EMPTY_DATE,
            };
          }

          // If there's an existing holiday at this position, keep it
          if (existingHoliday) {
            return {
              class: existingHoliday.class,
              date: existingHoliday.date,
            };
          }

          // Otherwise, return empty
          return {
            class: DEFAULT_CLASS,
            date: EMPTY_DATE,
          };
        });

        const payload = {
          dateNumber: String(selectedHoliday.value.dateNumber),
          name: selectedHoliday.value.name || "",
          class: selectedHoliday.value.class,
          date: selectedHoliday.value.date,
          dates: dates,
        };

        const response = await store.dispatch("dmp_panels/setHolidayDate", {
          uuid: props.uuid,
          holidayData: payload,
        });

        if (response?.success) {
          emit("operation-completed");

          showSuccess(
            isEditingHoliday.value
              ? proxy.$t("dmp.holidayDates.messages.updateSuccess")
              : proxy.$t("dmp.holidayDates.messages.saveSuccess")
          );
          selectedHoliday.value = null;
          isEditingHoliday.value = false;
        } else {
          showError(response?.message || proxy.$t("dmp.holidayDates.messages.saveError"));
        }
      } catch (error) {
        console.error("Error saving holiday:", error);
        emit("operation-error", error);
        showError(proxy.$t("dmp.holidayDates.messages.saveError"));
      } finally {
        store.commit("dmp_panels/setOperationActive", false);
        isSavingHoliday.value = false;
      }
    };

    watch(
      () => props.holidayDatesData,
      (newData) => {
        if (newData) {
          holidays.value = (newData.dates || []).map((date, index) => ({
            ...date,
            dateNumber: isTemplateMode.value ? index + 1 : date.dateNumber,
          }));
        }
      },
      { deep: true }
    );

    onMounted(() => {
      loadHolidays();
    });

    return {
      // Data
      holidays,
      displayHolidays,
      isLoadingHolidays,
      isSavingHoliday,
      isDeletingHoliday,
      isEditingHoliday,
      selectedHoliday,
      holidayFormObserver,
      holidayTableFields,
      classOptions,
      datePickerConfig,

      // Methods
      addNewHoliday,
      onRowSelected,
      deleteHoliday,
      saveHoliday,
      formatDateForDisplay,
      handleDateChange,

      // Form validation
      refFormObserver,
      getValidationState,

      // i18n
      t,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.card-header-custom {
  background-color: transparent;
}

.table-column,
.details-column {
  transition: all 0.3s ease-in-out;
}

.dark-layout {
  .card-header-custom {
    background-color: transparent !important;
  }
}

.gap-1 {
  gap: 0.25rem;
}

.mb-75 {
  margin-bottom: 0.75rem;
}

.font-weight-bold {
  font-weight: 600;
}
</style>
